// Role IDs
export const ROLES = {
  EXPERIMENTAL_OWNER:0,
  OWNER: 1,
  ADMIN: 2,
  HR: 3,
  OPERATIONS: 4,
  LEAD:5,
  FLEET_MANAGER: 8, 
  DRIVER: 10,
  // Provider roles (20-30)
  FLEET_PROVIDER_OWNER: 21,
  FLEET_PROVIDER_ADMIN: 22,
  FLEET_PROVIDER_TECHNICIAN: 23,
  FINANCE_PROVIDER_OWNER: 31,
  FINANCE_PROVIDER_ADMIN: 32,
  FINANCE_PROVIDER_ANALYST: 33,
  LINQOPS_SUPPORT_PROVIDER_OWNER: 201,
} as const;

// Role Names by ID
export const ROLE_NAMES: { [key: number]: string } = {
  0: 'Experimental Owner',
  1: 'Owner',
  2: 'Admin',
  3: 'HR',
  4: 'Operations',
  5: 'Lead',
  8: 'Fleet Manager',
  10: 'Driver',
  // Provider roles
  21: 'Fleet Provider Owner',
  22: 'Fleet Provider Admin',
  23: 'Fleet Provider Technician',
  31: 'Finance Provider Owner',
  32: 'Finance Provider Admin',
  33: 'Finance Provider Analyst',
  201: 'LinqOps Support Provider Owner',
};

/**
 * ROLE_RANGES.PROVIDER_* keys must stay in sync with PROVIDER_TYPES.
 * getProviderRoleRange builds the role-range key dynamically as `PROVIDER_${TYPE_KEY}`,
 * so whenever you add/update a provider type be sure to follow that naming convention.
 */
export const ROLE_RANGES = {
  PROVIDER: {
    MIN: 20,
    MAX: 209
  },
  PROVIDER_FLEET: {
    MIN: 20,
    MAX: 29
  },
  PROVIDER_FINANCE: {
    MIN: 30,
    MAX: 39
  },
  PROVIDER_LINQOPS_SUPPORT: {
    MIN: 201,
    MAX: 209
  }
};

// Provider Types
export const PROVIDER_TYPES = {
  FLEET: "fleet",
  FINANCE: "finance",
  LINQOPS_SUPPORT: "linqopsSupport"
} as const;

export const VALID_PROVIDER_TYPES = Object.values(PROVIDER_TYPES) as readonly string[];

// Firebase Chat Message Sender Types for LinqOps Support Provider
// This array includes all historical and current names for LinqOps Support messages
// Used for backward compatibility - when reading messages, check against this array
// When writing new messages, always use PROVIDER_TYPES.LINQOPS_SUPPORT
// 
// To rename LinqOps Support Provider in the future:
// 1. Update PROVIDER_TYPES.LINQOPS_SUPPORT to the new name
// 2. Add the old value to this array (e.g., "linqopsSupport" when renaming)
// 3. No other code changes needed - backward compatibility is automatic
export const LINQOPS_SUPPORT_PROVIDER_SENDER_TYPES = [
  PROVIDER_TYPES.LINQOPS_SUPPORT,  // Current name
  "linqopsAdmin"                   // Legacy name for backward compatibility
] as const;

// Employee Status
export const STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  OFFBOARDED: 'OFFBOARDED',
  'Background Check in Progress': 'Background Check in Progress',
} as const;

// Default Values
export const DEFAULTS = {
  TIMEZONE: 'America/New_York',
} as const;

// System User ID
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';
export const SYSTEM_USER_EMAIL = 'bot@linqops.com';

export const VEHICLE_STATUS = {
  INACTIVE: 'INACTIVE',
  ACTIVE: 'ACTIVE',
} as const;

export const VEHICLE_OPERATIONAL_STATUS = {
  OPERATIONAL: 'OPERATIONAL',
  GROUNDED: 'GROUNDED',
} as const;

export const EMPLOYEE_NOTES_TYPE = {
  ATTENDANCE: 'attendance',
  PERFORMANCE: 'performance',
  BEHAVIOR: 'behavior',
  FLEET: 'fleet',
  DOG_BITE: 'dog-bite',
  INCIDENT: 'incident',
  INJURY: 'injury',
  UNIFORM:'uniform',
  DRUG_TEST: 'drug-test',
  SCHEDULE:'schedule',
  TIMECARD: 'timecard',
  OTHER: 'other',
} as const;

// Attendance note type mapping for display labels
export const ATTENDANCE_TYPE_LABELS: Record<string, string> = {
  'call_out': 'Call Out',
  'late': 'Late',
  'no_show': 'No Show',
  'vto': 'VTO'
};

// Attendance note types
export const ATTENDANCE_NOTE_TYPES = {
  LATE: 'attendance/late',
  CALL_OUT: 'attendance/call_out',
  NO_SHOW: 'attendance/no_show',
  VTO: 'attendance/vto',
} as const;

export const APPLICANT_NOTES_TYPE = {
  ATTENDANCE: 'attendance',
  PERFORMANCE: 'performance',
  BEHAVIOR: 'behavior',
  FLEET: 'fleet',
  OTHER: 'other',
} as const;

export const FLEET_NOTES_TYPE = {
  REPAIR: "repair",
  PREVENTATIVE_MAINTENANCE: "preventative-maintenance",
  INSPECTION: "inspection",
  PAVE:"pave",
  ACCIDENT: "accident",
  DRIVER: "driver",
  DISPUTE:"dispute",
  TECHNICIAN: "technician",
  OTHER: "other",
};

export const OPERATION_DASHBOARD_OPTIONS = {
  ROUTES:"Routes",
  DAS:"DAs"
} as const;

export const FLEET_NOTES_SUB_TYPE = {
  DISPUTE: "dispute/afs",
};
export const FLEET_DOCUMENT_TYPE = {
  REGISTRATION: 'registration',
  INSURANCE: 'insurance',
  INSPECTION: 'inspection',
  RENTAL_AGREEMENT: 'rental-agreement',
  INVOICE: 'invoice',
  OTHER: 'other',
} as const;

export const EMPLOYEE_STATUS_TYPE = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  Offboarded: 'OFFBOARDED',
  Onboarding: 'ONBOARDING',
} as const;

export const getStatusText = (progressStatus: string): string => {
  switch (progressStatus) {
    case "not_applicable":
      return "Not Applicable";
    case "complete":
      return "Complete";
    case "ahead":
      return "Ahead";
    case "on_time":
      return "On Time";
    case "behind":
      return "Behind";
    case "at_risk":
      return "At Risk";
    case "late_departing":
      return "Late Departing";
    case "not_found":
      return "Not Found";
    default:
      return progressStatus;
  }
};

export const FLEET_STATUS_TYPE = {
  INACTIVE:'INACTIVE',
  OPERATIONAL:'OPERATIONAL',
  ACTIVE:'ACTIVE',
  GROUNDED:'GROUNDED'
}

export const PAYROLL_PROVIDER = {
  PAYCOM: "Paycom",
  ADP: "ADP",
};

export const ORGANIZATION_PAYROLL_PROVIDERS = {
  PAYCOM: "Paycom",
  ADP: "ADP",
  OTHER: "Other"
};

export const PAYROLL_PAYCODE = {
  PTO: "PTO",
  TRN: "TRN",
  TRAINING: "TRAINING"
};

export const PAYROLL_ID_STATUS_TYPE = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  Offboarded: 'OFFBOARDED',
}

export const CONTACT_SALES_CALENDLY_URL = "https://calendly.com/neelshah-connect/linqops-demo";
export const ONBOARDING_CALENDLY_URL = "https://calendly.com/linqops-sales/linqops-onboarding";

export const SUPPORT_PHONE = "+1 (888) 671-5467";
export const SUPPORT_EMAIL = "sales@linqops.com";

// Subscription Constants
export const SUBSCRIPTION = {
  FREE_TRIAL: {
    PLAN_TITLE: "Business Pro",
    DURATION_DAYS: 61, // Actual trial duration (61 days to ensure Stripe shows at least 60 days)
    DISPLAY_DURATION_DAYS: 60, // Marketing duration shown to users
    COUPON_NAME: "Free Trial",
    COUPON_DESCRIPTION: "100% off for 60-day free trial"
  }
}

export const DRUG_TEST_RESULT_OPTIONS = [
  "Ordered",
  "At Lab",
  "Negative",
  "Positive",
  "Repeat",
]

// Incident / Accident / Injury - dropdown options
export const INCIDENT_ACCIDENT_TYPE_OPTIONS = [
  "Bitten or stung",
  "Body position/posture; bend/lean/twist",
  "Caught in, under, on, between",
  "Climbing",
  "Contact by",
  "Contact with chemicals",
  "Contact with electrical",
  "Contact with glass",
  "Contact with sharp object",
  "Contact with substance",
  "Contact with temperature extremes",
  "Exposure to",
  "Falls, different level",
  "Falls same level",
  "Forceful exertion/pushing/pulling",
  "Lowering/lifting/carrying",
  "Motor vehicle accident",
  "Repetition",
  "Rubbed of abraded",
  "Slip/trip, no fall",
  "Stepped on",
  "Struct against",
  "Struct by",
];

export const INCIDENT_INJURY_OPTIONS = [
  "Abrasion, Scratches (superficial)",
  "Allergic Reaction",
  "Amputation",
  "Avulsion",
  "Bite, Sting (insect or animal)",
  "Blister",
  "Bloodborne Pathogen Exposure",
  "Burn - Chemical",
  "Burn - Electrical/Electrocution",
  "Burn or Scald(heat)",
  "Concussion",
  "Contusion, Bruise",
  "Crushing Injury",
  "Cyst",
  "Dislocation",
  "Drowning",
  "Eye Irritation",
  "Foreign Body(ie splinter,sliver,chip,dust)",
  "Fracture",
  "Frostbite/Hypertherma",
  "Headache",
  "Hearing Loss, or Impairment",
  "Hernia",
  "Herniated/Displaced/Ruptured Disc",
  "laceration, Cut, Open Wound",
  "loss Of Consciousness",
  "Multiple Serious Injuries, Catastrophic Event",
  "Muscle or Connective tissue Tear or Detachment",
  "Poisoning",
  "Puncture",
  "Radiation Effects, Non-Ionizing",
  "Respiratory Irritation (e.g., Asthma)",
  "Skin Irritation",
  "Sprain",
  "Strain",
  "Strangulation,Asphyxia",
  "Tooth Chip/Break",
];

export const INCIDENT_ILLNESS_OPTIONS = [
  "Asbestosis",
  "Black Lung",
  "Bursitis",
  "Cancer",
  "Carpel Tunnel Syndrome",
  "Conjunctivitis",
  "Contagious or Infectious Disease",
  "Dermatitis",
  "Epicondylitis",
  "Hearing Loss, or Impairment",
  "Heart Attack",
  "Heat Stress/Exhaustion/Stroke",
  "Hematuria",
  "Mental Stress",
  "Musculoskeletal Disorder, not otherwise listed",
  "Nerve Compression Impingement",
  "Occupational Disease/Illness, not otherwise listed",
  "Pneumoconiosis",
  "Poisonina",
];

export const INCIDENT_CORE_ACTIVITY_OPTIONS = [
  "Acting/Performing",
  "Asbestos Removal",
  "Assembly Operation",
  "Blasting",
  "Blending",
  "Brazing",
  "Business Meeting/Event/Travel",
  "Cafeteria Operations",
  "Chemical Production Operations",
  "Cleaning",
  "Compounding",
  "Confined Spaces Operations",
  "Construction",
  "Customer Assistance",
  "Cutting Operations",
  "Demolition",
  "Disassembly Operations",
  "Equipment De-Installment",
  "Equipment Installation",
  "Equipment Operation",
  "Flight Operation",
  "Forming",
  "House Keeping",
  "Inspecting",
  "Laboratory Operations",
  "Lifting",
  "Loading/Unloading",
  "Machining",
  "Maintenance",
  "Material Handling",
  "Non Specific Site Activity",
  "Office Work",
  "Package Delivery",
  "Packaging",
  "Pipeline Operation",
  "Plating",
  "Press Operation",
  "Repair",
  "Resident Assistance",
  "Security/Emergency Response Operation",
  "Shipping/Receiving",
  "Studio Operations",
  "Stunt Operation",
  "Surface Cleaning",
  "Surface Coating",
  "Testing",
  "Vehicle Operations",
  "Waste Management",
  "Welding",
  "Woodworking",
];

export const INCIDENT_JOB_ACTIVITY_TYPE_OPTIONS = [
  "Not a normal part of job",
  "Normal part of job, performed routinely",
  "Normal part of job but performed infrequently",
];

export const INCIDENT_TIME_ON_JOB_TASK_OPTIONS = [
  "0-6 months",
  "6-12 months",
  "1-5 years",
  "5-10 years",
  "10-20 years",
  ">20 years",
];

export const INCIDENT_YEARS_OF_AMZ_DSP_SERVICE_OPTIONS = [
  "0-6 months",
  "6-12 months",
  "1-5 years",
  "5-10 years",
  "10-20 years",
  ">20 years",
];

export const INCIDENT_UNSAFE_CONDITION_OPTIONS = [
  "Administrative work place control not followed",
  "Allergic Reaction",
  "Congested or tight place",
  "Defective/worn/maladjusted facil, vehicle",
  "Defective/worn/maladjusted tool/equipment",
  "Ergonomically inadequate workstation or rotation",
  "Excessive noise/vibration/light; inadequate light",
  "Exposure to Animals/Insects/Plants",
  "Exposure to bloodborne pathogens",
  "Exposure to Extreme Weather",
  "Exposure to ionizing radiation",
  "Exposure to non-ionizing radiation",
  "Exposure to Smoke, dust, fumes",
  "Fire or explosion hazard",
  "Guest/Visitor Contact",
  "High or low air temperature",
  "High or low surface temperature",
  "Housekeeping, unsafe placement, slip/trip hazards",
  "Improperly fitting parts, materials, fasteners",
  "Inadequate guards, barriers, or warning system",
  "Inadequate time allotted to task",
  "Inadequate/Faculty/No PPE Assigned or available",
  "Loss of Utilities",
  "Overpressure/Underpressure",
  "Sharp edge on part, workstation, or equipment",
  "Spilled/splashed chemicals or leaking containers",
  "Unsafe food or drink",
  "Wet, icy, snowy road or walkway",
];

export const INCIDENT_UNSAFE_ACT_OPTIONS = [
  "Ascending/descending stairs improperly",
  "Assigned rotation or rest intervals not used",
  "Did not heed warning signals, lights, sounds",
  "Employee awareness/attentiveness",
  "Employee in danger zone/Line of Fire",
  "Equipment/tool used improperly",
  "Failed to execute LOTO",
  "Failed to use proper fall protection",
  "Failed to warn or barricade",
  "Horseplay or distracting behavior",
  "Improper force/posture/position",
  "Improper lifting",
  "Improper loading (overloading of racks/vehicles)",
  "Improperly placed/secured matls, tools, furniture",
  "Inadequate checks for obstacles in vehicle path",
  "Inadequate clearance for safely stopping vehicle",
  "Inattention to established procedures(SOPs, JSAs)",
  "Operated vehicle improperly",
  "Proper PPE not used for task",
  "Removed or disabled safety devices or guards",
  "Used area not intended for walking/working",
  "Wrong/defective item used",
];

export const INCIDENT_JOB_FACTOR_OPTIONS = [
  "Inadequate communication",
  "Inadequate Control System Setup or Failure",
  "Inadequate engineering",
  "Inadequate inspection/auditing",
  "Inadequate job planning, instruction, supervision",
  "Inadequate Mechanical Integrity / Maintenance management",
  "Inadequate procedures/work instruction, work standards",
  "Inadequate sourcing/supplier control",
  "Inadequately maintained tools/equipment",
  "Knowing misuse or abuse",
  "Work environment not controlled by business",
];

export const INCIDENT_PERSONAL_BEHAVIORAL_FACTOR_OPTIONS = [
  "Difficult for employee to execute",
  "Human Error",
  "Not under direct control of employee",
  "Lack of knowledge or skill",
  "Physical capability",
  "Physical or psychological stress",
];

export const INCIDENT_WORK_STATUS = {
  RETURN_TO_FULL_DUTY: {
    key: "Return to Full Duty",
    certificationText:
      "I hereby certify that, The on-the-job injury that I have reported is not impacting my current ability to work and I affirm that I am healthy to continue working my current schedule without restrictions.",
  },
  RETURN_WITH_RESTRICTIONS: {
    key: "Return With Restrictions",
    certificationText:
      "I hereby certify that, The on-the-job injury that I have reported is impacting my current ability to work and I affirm that I am not healthy to continue working my current schedule without restrictions.",
  },
  EXCUSED_FROM_WORK_UNTIL_NEXT_APPOINTMENT: {
    key: "Excused From Work Until Next Appointment",
    certificationText:
      "I hereby certify that, The on-the-job injury that I have reported is impacting my current ability to work and I affirm that I am not healthy to continue working my current schedule without restrictions.",
  },
} as const;

export const INTEGRATIONS_TYPE = {
  PAYCOM: "paycom",
  ADP: "adp",
  ZOHO_MAIL: "zoho_mail",
}

// Integration credential field requirements
export const INTEGRATION_CREDENTIAL_FIELDS = {
  PAYCOM: {
    CREDENTIALS: ['sid', 'token'],
  },
  ADP: {
    CREDENTIALS: ['client_id', 'client_secret'],
    CERTIFICATES: ['certificate_pem', 'private_key'],
  },
}
// Notification throttling time in minutes
export const NOTIFICATION_THROTTLE_TIME_MINUTES = 15;

export const ORGANIZATION_TYPE = {
  PROVIDER: "provider",
}



// Email domain restriction for PROVIDER_LINQOPS_SUPPORT role
export const PROVIDER_LINQOPS_SUPPORT_EMAIL_DOMAIN = "@linqops.com";

// SMS-Related Keywords
// 10DLC Keywords
export const SMS_KEYWORDS = {
  OPT_IN: ['START','SUBSCRIBE'],
  OPT_OUT: ['STOP', 'UNSUBSCRIBE'],
  HELP: ['HELP'],
};

// Tollfree Keywords
export const TOLLFREE_SMS_KEYWORDS = {
  OPT_IN: ['START','UNSTOP'],
  OPT_OUT: ['STOP'],
  HELP: ['HELP'],
};

// Tenure Filter Ranges (in months)
export const TENURE_FILTERS = {
  NEW_HIRE: {
    MIN: 0,
    MAX: 2,
    LABEL: '0-2 months'
  },
  EARLY_TENURE: {
    MIN: 2,
    MAX: 4,
    LABEL: '2-4 months'
  },
  MID_TENURE: {
    MIN: 4,
    MAX: 6,
    LABEL: '4-6 months'
  },
  EXPERIENCED: {
    MIN: 6,
    MAX: null, // No upper limit
    LABEL: '6+ months'
  },
  OTHER: {
    LABEL: 'Others' // For cases where hire date is missing or invalid
  }
};

// Sources to exclude from Netradyne violations (considered non-Netradyne)
export const EXCLUDED_NETRADYNE_VIOLATION_SOURCES = ["Geotab"];

export const EMPLOYEE_VAN_PREFERENCE = {
  SELECT: "Select",
  AMAZON_BRANDED: "Amazon Branded",
  RENTAL: "Rental",
}

// Payroll Sync Status Constants
export const PAYROLL_SYNC_TYPE = {
  ADP: "adp",
  PAYCOM: "paycom",
};

// Payroll Sync Schedule Hours (UTC times for cron jobs)
// Paycom runs at 13:00 and 00:00 UTC
// ADP runs at 14:00 and 01:00 UTC
export const PAYROLL_SYNC_SCHEDULE_HOURS = {
  PAYCOM: [13, 0], // 13:00 and 00:00 UTC
  ADP: [14, 1], // 14:00 and 01:00 UTC
} as const;

export const PAYROLL_SYNC_STATUS = {
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
};

// Cron Job Constants
export const CRON_JOB_TYPES = {
  CREATE_AUTOMATED_COACHING_TICKETS: "createAutomatedCoachingTickets",
  ALERT_ENABLE_NOTIFICATION: "alertEnableNotification",
  WEEKLY_PERFORMANCE_NOTES: "weeklyPerformanceNotes",
  SEND_ALERTS_FOR_SCHEDULE: "sendAlertsForSchedule",
  AI_BATCH_INFERENCE: "aiBatchInference",
} as const;

export const CRON_JOB_STATUS = {
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

// Notification Type Constants
export const MANAGEMENT_NOTIFICATION_TYPES = {
  CHAT: 'chat',
  EMPLOYEE_NOTES: 'employeeNotes',
  FLEET_NOTES: 'fleetNotes',
  FLEET_STATUS: 'fleetStatus',
} as const;

// Notification Chat Source Constants
export const MANAGEMENT_NOTIFICATION_CHAT_SOURCES = {
  DISPATCH: 'dispatch',
  HR: 'hr',
} as const;

// Management Notification Types Configuration
export const MANAGEMENT_NOTIFICATION_TYPES_CONFIG = {
  [MANAGEMENT_NOTIFICATION_TYPES.CHAT]: {
    label: 'Chat',
    sources: [MANAGEMENT_NOTIFICATION_CHAT_SOURCES.DISPATCH, MANAGEMENT_NOTIFICATION_CHAT_SOURCES.HR]
  },
  [MANAGEMENT_NOTIFICATION_TYPES.EMPLOYEE_NOTES]: {
    label: 'Employee Notes'
  },
  [MANAGEMENT_NOTIFICATION_TYPES.FLEET_NOTES]: {
    label: 'Fleet Notes'
  },
  [MANAGEMENT_NOTIFICATION_TYPES.FLEET_STATUS]: {
    label: 'Fleet Status'
  }
} as const;

export const FIREBASE_APP_INSTANCE_NAMES = {
  DEFAULT: "[DEFAULT]",
  ADMIN_SUPPORT: "adminSupport",
}

// Violation color constants for dispatch page tags
export const VIOLATION_COLORS = {
  DSB: {
    background: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200'
  },
  CDF: {
    background: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  SAFETY: {
    background: 'bg-pink-100',
    text: 'text-pink-800',
    border: 'border-pink-200'
  },
  CC: {
    background: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200'
  },
  HOURS_EXCEEDED: {
    background: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200'
  },
  OODT: {
    background: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200'
  },
  OVERTIME: {
    background: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200'
  },
  // Add more violation types as needed
} as const;

export type ViolationType = keyof typeof VIOLATION_COLORS;

// Performance labels configuration
export const PERFORMANCE_LABELS = {
  CC: {
    key: 'CC', // Short key shown in dispatch page
    label: 'Contact Compliance', // User-friendly name for settings checkboxes
    description: 'Issues with customer contact procedures (missing exemptions, incomplete communication attempts)',
  },
  OODT: {
    key: 'OODT',
    label: 'Out of Drive Time',
    description: 'Driver activity recorded outside of scheduled drive time windows',
  },
  DSB: {
    key: 'DSB',
    label: 'Delivery Success Behavior',
    description: 'Behavioral issues affecting delivery success and performance metrics',
  },
  CDF: {
    key: 'CDF',
    label: 'Customer Delivery Feedback',
    description: 'Negative feedback received from customers about delivery experience',
  },
  SAFETY: {
    key: 'SAFETY',
    label: 'Safety Violations',
    description: 'Traffic safety violations (speeding, stop signs, traffic lights, following distance)',
  },
  HOURS_LIMIT: {
    key: 'hours', // Will be prefixed with ">" in dispatch
    label: 'Hours Limit Exceeded',
    description: 'Drivers working more than the configured maximum hours on last working day',
  },
  OVERTIME: {
    key: 'OT', // Will be suffixed with threshold in dispatch
    label: 'Overtime',
    description: 'Drivers approaching or exceeding weekly overtime thresholds',
  },
} as const;

export type PerformanceLabelsType = keyof typeof PERFORMANCE_LABELS;

// Coaching Settings Constants
export const COACHING_THRESHOLD_UNITS = {
  DAY: 'day',
  WEEK: 'week'
} as const;

export const COACHING_THRESHOLD_UNIT_OPTIONS = Object.values(COACHING_THRESHOLD_UNITS);

export const EXPERIMENTAL_ORG_IDS = [
  '25b5e67b-4064-4e67-9d75-151e4f8e19ff', // LQLC
  'c28fd1d7-4518-463b-a609-ed8cfb99240d', // LMDL
  '1fc07e27-9784-4926-ae23-9a251ffd1867' // DEMO
] as const;

export const COACHING_CATEGORIES = {
  QUALITY: {
    key:"Quality",
    subCategories: { 
      CDF: {
        key:'CDF',
        infractionTypes:{
          MISHANDLED_PACKAGE:{
            key:'Mishandled package',
          },
          UNPROFESSIONAL:{
            key:'Unprofessional',
          },
          NOT_FOLLOW_INSTRUCTION:{
            key:'Not follow Instruction',
          },
          WRONG_ADDRESS:{
            key:'Wrong Address',
          },
          NEVER_RECEIVED:{
            key:'Never Received',
          },
          WRONG_ITEM:{
            key:'Wrong Item',
          }
        }
      },
      DSB: {
        key:'DSB',
        infractionTypes:{
          SIMULTANEOUS_DELIVERIES:{
            key:'Simultaneous Deliveries',
          },
          DELIVERIES_50_METERS:{
            key:'Deliveries > 50 Meters',
          },
          INCORRECT_SCAN_USAGE_AD:{
            key:'Incorrect Scan Usage – AD',
          },
          INCORRECT_SCAN_USAGE_UD:{
            key:'Incorrect Scan Usage – UD',
          },
          NO_POD_DELIVERY:{
            key:'No POD on Delivery',
          },
          SNDNR:{
            key:'SNDNR',
          }
        }
      },
      DCR: {
        key:'DCR',
        infractionTypes:{
          CONTACT_COMPLIANT:{
            key:'Contact Compliant',
          },
          OODT: {
            key: 'OODT'
          },
          RTS_OTHER: {
            key: 'RTS - OTHER'
          }
        }
      }
    }
  },
  SAFETY: {
    key:"Safety",
    subCategories: {
      Netradyne: {
        key:'Netradyne',
        infractionTypes:{
          HIGH_G:{
            key:'High G',
          },
          LOW_IMPACT:{
            key:'Low Impact',
          },
          POTENTIAL_COLLISION:{
            key:'Potential Collision',
          },
          BACKING:{
            key:'Backing',
          },
          SIGN_VIOLATIONS:{
            key:'Sign Violations',
          },
          TRAFFIC_LIGHT_VIOLATIONS:{
            key:'Traffic Light Violations',
          },
          DRIVER_DISTRACTION:{
            key:'Driver Distraction',
          },
          FOLLOWING_DISTANCE:{
            key:'Following Distance',
          },
          SPEEDING_VIOLATIONS:{
            key:'Speeding Violations',
          },
          SEATBELT_COMPLIANCE:{
            key:'Seatbelt Compliance',
          },
          U_TURN:{
            key:'U Turn',
          },
          HARD_BRAKING:{
            key:'Hard Braking',
          },
          HARD_TURN:{
            key:'Hard Turn',
          },
          HARD_ACCELERATION:{
            key:'Hard Acceleration',
          },
          DRIVER_DROWSINESS:{
            key:'Driver Drowsiness',
          },
          WEAVING:{
            key:'Weaving',
          },
          CAMERA_OBSTRUCTION:{
            key:'Camera Obstruction',
          }
        }
      },
      Geotab: {
        key:'Geotab',
        infractionTypes:{
          SEATBELT:{
            key:'Seatbelt',
          },
          SPEEDING:{
            key:'Speeding',
          }
        }
      },
      eMentor: {
        key:'eMentor',
        infractionTypes:{
          FICO_SCORE:{
            key:'FICO Score',
          },
          BRAKING:{
            key:'Braking',
          },
          IDLING:{
            key:'Idling',
          },
          CORNERING:{
            key:'Cornering',
          },
          DISTRACTION:{
            key:'Distraction',
          },
          BACKUP:{
            key:'BackUp',
          },
          SPEEDING:{
            key:'Speeding',
          },
          ACCELERATION:{
            key:'Acceleration',
          },
          EOC:{
            key:'EOC',
          },
          MENTOR_TRAINING_VIDEOS:{
            key:'Mentor Training Videos',
          },
          SPEEDING_EVENT_RATE:{
            key:'Speeding Event Rate',
          },
          SEATBELT_OFF_RATE:{
            key:'Seatbelt Off Rate',
          }
        }
      }
    }
  },
  DSP_SPECIFIC: {
    key:"DSP Specific",
    subCategories: {
      Attendance: {
        key:'Attendance',
        infractionTypes:{
          TARDINESS:{
            key:'Tardiness (Late Arrival)',
          },
          LATE_SCHEDULE_CHANGE_REQUEST:{
            key:'Late Schedule Change Request',
          },
          LATE_TIME_OFF_REQUEST:{
            key:'Late Time-off Request',
          },
          WEEKEND_OFF_REQUEST:{
            key:'Weekend Off Request',
          },
          CALL_OUT:{
            key:'Call Out',
          },
          NO_SHOW:{
            key:'No Show',
          }
        }
      },
      Behavior: {
        key:'Behavior',
        infractionTypes:{
          MEAL_BREAK:{
            key:'Meal Break',
          },
          GAS_REFILL:{
            key:'Gas Refill',
          },
          MISS_PUNCH:{
            key:'Miss Punch (In/Out or Break)',
          },
          TRASH_IN_VAN:{
            key:'Trash in Van',
          },
          DVIC:{
            key:'DVIC',
          },
          MENTOR_PRE_POST_INSPECTION:{
            key:'Mentor Pre-Post Inspection',
          },
          MISSING_PACKAGES_START_ROUTE:{
            key:'Missing Packages at Start of Route',
          }
        }
      },
      Performance: {
        key:'Performance',
        infractionTypes:{
          PERFORMANCE_ROUTE:{
            key:'Performance of Route',
          },
          EMENTOR:{
            key:'eMentor',
          },
          UNPLANNED_STOPS:{
            key:'Unplanned Stops',
          },
          TIME_OF_TASK_TIME_THEFT:{
            key:'Time Of Task/Time Theft',
          },
          ACCIDENTS:{
            key:'Accidents',
          },
          STUCK_IN_MUD:{
            key:'Stuck in Mud',
          }
        }
      }
    }
  }
} as const

// Incident Question Bank Constants
export const INCIDENT_TYPES = {
  INCIDENT: "incident",
  ACCIDENT: "accident", 
  INJURY: "injury"
} as const;

export const INCIDENT_UPLOAD_TYPES = {
  DOCUMENTS: "documents",
  VIDEOS: "videos",
  DOCTORS_NOTE: "doctorsNote",
} as const;

export type IncidentUploadType =
  typeof INCIDENT_UPLOAD_TYPES[keyof typeof INCIDENT_UPLOAD_TYPES];

export const INCIDENT_UPLOAD_TYPE_LABELS: Record<IncidentUploadType, string> = {
  [INCIDENT_UPLOAD_TYPES.DOCUMENTS]: "documents",
  [INCIDENT_UPLOAD_TYPES.VIDEOS]: "videos",
  [INCIDENT_UPLOAD_TYPES.DOCTORS_NOTE]: "doctor's note",
} as const;

// Help Desk Ticket Types
// Used for user-created support requests (Help → Contact Support page).
export const HELP_DESK_TICKET_TYPES = {
  BILLING: "billing",
  BUG: "bug",
  FEATURE_REQUEST: "feature_request",
  ACCOUNT_ACCESS: "account_access",
  INTEGRATION: "integration",
  OTHER: "other",
} as const;

export const HELP_DESK_TICKET_TYPE_OPTIONS = Object.values(
  HELP_DESK_TICKET_TYPES,
) as readonly string[];

export const USAGE_LOCATIONS = {
  INCIDENT_DETAIL: "incident_detail",
  MANAGER_REPORT: "manager_report",
  DRIVER_RESPONSE: "driver_response"
} as const;

export const ANSWER_TYPES = {
  BOOLEAN: "boolean",
  TEXT: "text",
  TEXTAREA: "textarea",
  SELECT: "select",
  DATE: "date",
  NUMBER: "number"
} as const;

// Corrective Action Constants
export const CORRECTIVE_ACTION_SOURCES = {
  CATEGORY: "category",
  SUB_CATEGORY: "subCategory",
  INFRACTION_TYPE: "infractionType"
} as const;

export const CORRECTIVE_ACTION_SOURCE_OPTIONS = Object.values(CORRECTIVE_ACTION_SOURCES);

export const CORRECTIVE_ACTION_WARNING_LEVELS = {
  FIRST_WARNING: {
    key: "first_warning",
    label: "First Warning"
  },
  SECOND_WARNING: {
    key: "second_warning",
    label: "Second Warning"
  },
  THIRD_WARNING: {
    key: "third_warning",
    label: "Third Warning"
  },
  FINAL_WARNING: {
    key: "final_warning",
    label: "Final Warning"
  },
  SUSPENSION: {
    key: "suspension",
    label: "Suspension"
  },
  TERMINATION: {
    key: "termination",
    label: "Termination"
  }
} as const;

export const CORRECTIVE_ACTION_WARNING_LEVEL_OPTIONS = Object.values(CORRECTIVE_ACTION_WARNING_LEVELS);

export const CORRECTIVE_ACTION_UNITS = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month"
} as const;

export const CORRECTIVE_ACTION_STATUS = {
  OPEN: {
    key: "open",
    label: "Open"
  },
  CLOSED: {
    key: "closed",
    label: "Closed"
  },
  DELETED: {
    key: "deleted",
    label: "Deleted"
  }
} as const;

export const CORRECTIVE_ACTION_UNIT_OPTIONS = Object.values(CORRECTIVE_ACTION_UNITS);

/**
 * Default per-service-area schedule settings used when creating a new organization.
 * Mirrors the previous implicit DB defaults of the deprecated scalar columns
 * (defaultStandupTime="45", singleStandup=true, backupClockIn="1") so that new
 * orgs satisfy the schedule-settings page validator on first visit.
 */
export const SCHEDULE_SETTING_DEFAULTS = {
  defaultStandupTimeMinutes: "45",
  isSingleStandup: true,
  backupClockInWave: "1",
} as const;

// Dispatch Notification Status Constants
export const DISPATCH_NOTIFICATION_STATUS = {
  SENT: 'sent',
  FAILED: 'failed',
  NOT_SENT: 'not_sent',
  PENDING: 'pending',
} as const;

export type DispatchNotificationStatus = typeof DISPATCH_NOTIFICATION_STATUS[keyof typeof DISPATCH_NOTIFICATION_STATUS];

// Tomorrow Dispatch Warning Messages
export const TOMORROW_DISPATCH_WARNINGS = {
  NO_EMPLOYEES_SCHEDULED: 'Warning: No employees are scheduled for tomorrow.',
  NO_ROSTER_INFO: 'Warning: We do not have roster data available for drivers for tomorrow.',
  NO_ROUTE_TARGET: 'Warning: We do not have route target data for tomorrow.',
} as const;